// Michigan State University: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - MSU's Common Data Set 2025-2026 (cds-2025-2026.pdf), the file MSU
//    Institutional Research links from its Common Data Set page. The PDF is
//    served from MSU's Sitecore media host (edge.sitecorecloud.io), so the
//    SOURCES url is the ir.msu.edu page that links it. C1 applicants, admits
//    and enrollees for Fall 2025 with the residency table; C8 test policy for
//    Fall 2027; C21/C22 plans; G1/G5 2026-2027 costs.
//  - admissions.msu.edu: First-year applicants (the banner says the fall
//    2027 first-year application is open), First-year dates and deadlines,
//    ACT/SAT optional, and How to apply. The deadline page carries no year;
//    it is the page as posted for the fall 2027 application.
//  - College Scorecard, school id 171100 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost: the CDS G1/G5 first-year column is used. MSU's G1 tuition is higher
// for students past the first year ($19,284 in-state, $45,636 out-of-state),
// which the cost note says. Food and housing is the CDS's on-campus figure
// (housing $5,338 plus food $7,590).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'MSU Common Data Set 2025-2026',
        url: 'https://ir.msu.edu/cds',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'MSU Admissions, First-year applicants',
        url: 'https://admissions.msu.edu/apply/first-year',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'MSU Admissions, First-year dates and deadlines',
        url: 'https://admissions.msu.edu/apply/first-year/dates-and-deadlines',
        read: '2026-10-01',
    },
    tests: {
        label: 'MSU Admissions, ACT/SAT optional',
        url: 'https://admissions.msu.edu/apply/first-year/act-sat-test-optional',
        read: '2026-10-01',
    },
    howToApply: {
        label: 'MSU Admissions, How to apply',
        url: 'https://admissions.msu.edu/apply/first-year/how-to-apply',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?171100-Michigan-State-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'michigan-state-university',
    kbId: 'michigan_state_university',
    name: 'Michigan State University',
    shortName: 'Michigan State',
    aliases: ['MSU'],
    location: 'East Lansing, MI',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (MSU brand color palette); decoration only.
    branding: {
        primary: '#18453B',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/michigan_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 62965,
        admitted: 52411,
        enrolled: 9355,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 25102, admitted: 21067 },
            { group: 'Out-of-state', applied: 30768, admitted: 25087 },
            { group: 'International', applied: 7095, admitted: 6257 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'MSU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'MSU\'s early action plan is not binding. The Common Data Set lists its dates (apply by November 1, notified by January 15) but no early decision plan, and it does not report how many applied or were admitted early, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'As a first-year applicant you do not declare a major yet, but the application asks for a major preference ("Exploratory preference" if you are undecided):',
        rows: [
            { school: 'Major preference', requirement: 'Fall first-year applicants can change their major preference in their account until May 1. Anyone who applies or is admitted after May 1 cannot change it until New Student Orientation.' },
        ],
        note: 'Every applicant writes one essay of 250 to 650 words from MSU\'s list of prompts. Letters of recommendation are not required. The application fee is $75.',
        source: 'howToApply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Early action', value: 'November 1' },
        rows: [
            { plan: 'Early action', deadline: 'November 1', decision: 'By January 15', binding: false },
            { plan: 'Regular decision, priority date', deadline: 'February 1', decision: 'By March 31', binding: false },
            { plan: 'Regular decision, final deadline', deadline: 'April 1', decision: null, binding: false },
        ],
        note: 'Early action means your application and all required materials are in by November 1 and you chose the early action option on the application; it also gives you maximum scholarship consideration. Regular decision applications are reviewed on a rolling basis, and those received between February 1 and April 1 get no set decision date. Admitted students accept and pay the acceptance fee by May 1.',
        source: 'deadlines',
        binding: 'Early action is non-binding: if admitted, you are not required to attend MSU.',
        bindingSource: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'MSU is test-optional: you choose on the application whether your SAT or ACT scores are part of your review, and you may self-report them. MSU considers your highest total SAT or composite ACT score and does not combine section scores from different sittings. Admitted students who entered their own scores must send official results before New Student Orientation.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by June 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition (first year)', amounts: [16916, 44300] },
            { item: 'Required fees', amounts: [340, 340] },
            { item: 'Food and housing (on campus)', amounts: [12928, 12928] },
            { item: 'Books and supplies', amounts: [1420, 1420] },
            { item: 'Transportation', amounts: [1310, 1310] },
            { item: 'Other expenses', amounts: [3930, 3930] },
        ],
        totals: [36844, 64228],
        note: 'For a full-time first-year student living on campus, fall and spring. Tuition is higher after the first year: $19,284 in-state and $45,636 out-of-state for other undergraduates.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 21322,
            byIncome: [
                { income: '$0 to $30,000', amount: 11741 },
                { income: '$30,001 to $48,000', amount: 11883 },
                { income: '$48,001 to $75,000', amount: 14300 },
                { income: '$75,001 to $110,000', amount: 22082 },
                { income: 'Over $110,000', amount: 30356 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from MSU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
